import { BehaviorSubject, Observable } from 'rxjs';
import _ from 'lodash';


export interface IStore<T> {
  selectAll(): Observable<T[]>;
  selectActive(): Observable<T>;
  getAll(): T[];
  get(item: number | string | any): T;
  getActive(): T;
  set(entities: T[]): void;
  upsert(condition: (pre: T) => T, key: number | string): void;
  upsert(entity: Partial<T>): void;
  upsertMany(items: Partial<T>[]): void;
  update(condition: (pre: T) => T, key: number | string): void;
  update(entity: Partial<T>): void;
  updateMany(items: Partial<T>[]): void;
  add(entity: T): void;
  remove(entity: T): void;
  remove(key: string | number): void;
  remove(condition: (item: T) => boolean): void;
  reset(): void;
}

export class LocalStoreBase<T> implements IStore<T>{
  _store: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  _active: BehaviorSubject<T> = new BehaviorSubject<T>(null);
  _keyName: string = 'id';

  _store$ = this._store.asObservable();
  _active$ = this._active.asObservable();

  _getKey(entity: T) {
    return entity?.[this._keyName];
  }

  _isEqual(a: T, b: T) {
    return a == b || this._getKey(a) === this._getKey(b);
  }

  _refreshEntities(entities: T[], force: boolean = true) {
    if (force) {
      this._store.next([...entities]);
    } else {
      this._store.next(entities);
    }
  }

  _removeByKey(key: string | number) {
    const entities = this.getAll();
    const existing = this.get(key);
    if (existing != null) {
      const index = entities.indexOf(existing);
      entities.splice(index, 1);
      this._refreshEntities(entities);
      if (this._isEqual(existing, this.getActive())) {
        this._active.next(null);
      }
    }
  }
  _removeByCondition(condition: (item: T) => boolean) {
    const entities = this.getAll().filter(it => !condition(it));
    this._refreshEntities(entities);
    if (entities.indexOf(this.getActive()) >= 0) {
      this._active.next(null);
    }

  }
  _removeByItem(item: T) {
    this._removeByKey(this._getKey(item));
  }
  _upsertEntity(entity: Partial<T>) {
    const entities = this.getAll();
    const existing = this.get(this._getKey(entity as T));
    if (existing != null) {
      const index = entities.indexOf(existing);
      const clone = _.cloneDeep(existing);
      _.mergeWith(clone, entity, (to, from) => { if (Array.isArray(to) || Array.isArray(from)) { return from; } });
      entities.splice(index, 1, clone);
      this._refreshEntities(entities);
      if (this._isEqual(existing, this.getActive())) {
        this._active.next(clone);
      }
    } else {
      this.add(entity as T);
    }
  }

  constructor(keyName: string = 'id') {
    this._keyName = keyName;
  }


  selectAll() {
    return this._store$;
  }
  selectActive() {
    return this._active$;
  }

  reset() {
    this._store.next([]);
    this._active.next(null);
  }
  set(entities: T[]) {
    this._refreshEntities(entities);
    this._active.next(null);
  }
  upsert(condition: (pre: T) => T, key: number | string);
  upsert(entity: Partial<T>);
  upsert(param: Partial<T> | ((pre: T) => T), key?: number | string) {
    if (typeof param === 'function') {
      const entities = this.getAll();
      const existing = this.get(key);
      if (existing) {
        const index = entities.indexOf(existing);
        entities.splice(index, 1, param(existing));
        this._refreshEntities(entities);
        if (this._isEqual(existing, this.getActive())) {
          this._active.next(entities[index]);
        }
      }
    } else {
      this._upsertEntity(param);
    }
  }
  update(condition: (pre: T) => T, key: number | string);
  update(entity: Partial<T>);
  update(param: Partial<T> | ((pre: T) => T), key?: number | string) {
    if (typeof param === 'function') {
      const entities = this.getAll();
      const existing = this.get(key);
      if (existing) {
        const index = entities.indexOf(existing);
        entities.splice(index, 1, param(existing));
        this._refreshEntities(entities);
        if (this._isEqual(existing, this.getActive())) {
          this._active.next(entities[index]);
        }
      }
    }
  }
  upsertMany(items: Partial<T>[]) {
    const entities = this.getAll();
    for (let item of items) {
      let existing = entities.find(it => this._isEqual(it, item as any));
      if (existing != null) {
        const clone = _.cloneDeep(existing);
        _.mergeWith(clone, item, (to, from) => { if (Array.isArray(to) || Array.isArray(from)) { return from; } });
        const index = entities.indexOf(existing);
        entities.splice(index, 1, clone);
      } else {
        entities.push(item as T);
      }
    }
    this._refreshEntities(entities);
    const active = this.getActive();
    if (active != null) {
      const fromStore = entities.find(it => this._isEqual(it, active));
      if (fromStore != active) {
        this._active.next(fromStore);
      }
    }
  }

  updateMany(items: Partial<T>[]) {
    const entities = this.getAll();
    for (let item of items) {
      let existing = entities.find(it => this._isEqual(it, item as any));
      if (existing != null) {
        const clone = _.cloneDeep(existing);
        _.mergeWith(clone, item, (to, from) => { if (Array.isArray(to) || Array.isArray(from)) { return from; } });
        const index = entities.indexOf(existing);
        entities.splice(index, 1, clone);
      }
    }
    this._refreshEntities(entities);
    const active = this.getActive();
    if (active != null) {
      const fromStore = entities.find(it => this._isEqual(it, active));
      if (fromStore != active) {
        this._active.next(fromStore);
      }
    }
  }

  add(entity: T) {
    const entities = this._store.getValue();
    entities.push(entity);
    this._refreshEntities(entities);
  }
  remove(entity: T);
  remove(key: string | number);
  remove(condition: (item: T) => boolean);
  remove(keys: string[] | number[]);
  remove(param: T | string | number | ((item: T) => boolean) | (string[] | number[])) {
    if (typeof param === 'string' || typeof param === 'number') {
      this._removeByKey(param);
    } else if (typeof param === 'function') {
      this._removeByCondition(param as any);
    } else if (Array.isArray(param)) {
      this._removeByCondition((item: T) => param.findIndex(it => this._isEqual(it, item)) >= 0);
    }
    else {
      this._removeByItem(param);
    }
  }

  getAll() {
    return this._store.getValue() || [];
  }
  get(key: number | string) {
    return this.getAll().find(it => this._getKey(it) === key);
  }
  getActive() {
    return this._active.getValue();
  }

}